<template>
    <div class="wrapper">
        <div class="top_logo">
            <img src="~@/assets/images/logo2.png" alt="">
        </div>

        <form-wizard @on-change="onChange"
                     :color="'#43587F'"
                     :errorColor="'#354665'"
                     title="2 Factor Authentication setup"
                     subtitle=""
                     shape="circle"
                     :nextButtonText="this.nextButtonText"
                     ref="wizard"
        >
            <tab-content title="Installing Authenticator app">
                <p>Please install google authenticator on your mobile. When the <br>installation is done, click on {{this.nextButtonText}}.</p>
                <div class="store-btn">
                    <small>Choose your mobile platform</small>
                    <ul>
                        <li><a href="https://itunes.apple.com/us/app/google-authenticator/id388497605?mt=8" target="_blank"><img src="~@/assets/images/app_store.png"></a></li>
                        <li><a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en" target="_blank"><img src="~@/assets/images/play_button.png"></a></li>
                    </ul>
                </div>
            </tab-content>
            <tab-content title="Synchronize">
                <div class="row">
                    <div class="qr-code col-md-4">
                        <img :src="this.qrCode" alt="">
                    </div>
                    <div class="qr-text col-md">
                        <p>Scan the QR Code below to start the synchronisation. When the synchronisation is done, click on continue.</p>
                        <small>Or you can enter the code manually in <br>Google Authenticator.</small>
                        <div>{{this.tfaSecret}}</div>
                    </div>
                </div>
            </tab-content>
            <tab-content title="Validation" :before-change="validateCode">
                <p>Enter the verification code generated by<br>
                    Google Authenticator for your account and click continue.</p>
                <div class="errors" v-if="$validator.errors">
                    <small v-for="(error, index) in $validator.errors.items" v-bind:key="index">
                        {{ error.msg }}
                    </small>
                </div>
                <div class="tfaCodeWrapper">
                    <custom-input :validationRules="{required: true}" type="text" name="tfaCode" id="tfaCode" ref="tfaCode" data-vv-as="2Factor Authentication code" v-model="tfaCode" placeholder="Input code here" autocomplete="off" />
                </div>
            </tab-content>
            <tab-content title="Recovery codes">
                <div id="top-logo">
                    <img src="~@/assets/images/logo2.png" class="col-centered" alt="">
                </div>
                <p>If you are ever unable to use your Google Authenticator app,<br>
                    the following six unique recovery codes will enable you to connect to your account.</p>
                <div id="recovery-codes">
                    <small><span>Please make sure you save and store them properly.</span></small>
                    <ul>
                        <li v-for="(code, index) in this.tfaRecoveryCodes" v-bind:key="index">
                            {{code}}
                        </li>
                    </ul>
                </div>
            </tab-content>

            <template slot="footer" slot-scope="props">
                <div class="wizard-footer-left">
                    <div v-if="onCancel && props.activeTabIndex === 0">
                        <wizard-button @click.native="onCancel()" class="cancel-btn">Cancel</wizard-button>
                    </div>
                    <div v-if="props.activeTabIndex > 0">
                        <wizard-button @click.native="props.prevTab()" class="previous-btn">Previous</wizard-button>
                    </div>
                </div>
                <div class="wizard-footer-right">
                    <wizard-button v-if="$refs['wizard'] ? !$refs['wizard'].isLastStep : false" @click.native="props.nextTab()" class="wizard-footer-right next-btn">Next</wizard-button>

                    <wizard-button v-else class="wizard-footer-right finish-btn" @click.native="onCompleteLocal">Finish</wizard-button>
                </div>
            </template>
        </form-wizard>
    </div>
</template>

<script>
import { Default } from '@/mixins/mixins';
import '@/assets/styles/vue-form-wizard.min.css';
import { FormWizard, TabContent, WizardButton } from 'vue-form-wizard';
import CustomInput from '@/components/additional/customInput';
import QRCode from 'qrcode';

export default {
    name: 'tfaAppSetup',
    mixins: [
        Default
    ],
    components: {
        CustomInput,
        FormWizard,
        TabContent,
        WizardButton
    },
    props: [
        'onCancel',
        'onComplete'
    ],
    inject: {
        $validator: '$validator'
    },
    data() {
        return {
            qrCode: '',
            tfaCode: '',
            nextButtonText: 'Next',
            tfaSecret: null,
            tfaVerifyHash: null,
            tfaRecoveryCodes: []
        };
    },
    mounted() {
        this.generateQrCode();
    },
    computed: {
        token: {
            get() {
                const getTemp = this.$store.getters[this.$store.types.token.getters.get_temp];
                return getTemp ? getTemp.token : this.$store.getters[this.$store.types.token.getters.get_nontemp].token;
            }
        }
    },
    methods: {
        onCompleteLocal: function() {
            if (this.onComplete)
                this.onComplete();
            else
                this.$router.push('/accounts/list'); // Redirect to 2Factor
        },
        onChange: function(prevIndex, nextIndex) {
            if (nextIndex === 2 && prevIndex === 1) // Step 3 is validation but here we start at 0 so it's 2
                setTimeout(() => { this.$refs.tfaCode.$el.querySelector('input').focus(); }, 20); // Without a small delay the focus won't work...
        },
        validateCode: function() {
            return new Promise((resolve, reject) => {
                this.$parleyAdmin.validateMultiple([this.$refs.tfaCode], function(value) {
                    if (value)
                        this.checkTfaCode().then(resolve);
                    else
                        reject();
                }.bind(this));
            });
        },
        generateQrCode: function() {
            // Get secret from API GET /auth/2fa/secret
            this.getTfaSecret().then(tfaInfo => {
                this.tfaSecret = tfaInfo.secret;
                this.tfaVerifyHash = tfaInfo.verifyHash;

                const label = encodeURI('Parley Admin interface');
                const user = this.$store.getters[this.$store.types.user.getters.get].email;
                const key = this.tfaSecret;
                const protocol = 'otpauth://totp/';
                const url = protocol + label + ':' + user + '?secret=' + key + '&issuer=' + label;

                // Create QR code from that secret so that readers can read it
                QRCode.toDataURL(url).then(url => {
                    this.qrCode = url;
                }).catch(() => {
                    this.$parleyAdmin.queryErrors.addError('Something went wrong while generating a QR Code, please try again later');
                });
            });
        },
        getTfaSecret: function() {
            return this.$http.get('v1.1/auth/2fa/secret', {
                params: {
                    authorization: this.token
                }
            }).then(response => {
                // Success
                return {secret: response.body.data.secret, verifyHash: response.body.data.verifyHash};
            }, response => {
                // Error
                this.$parleyAdmin.queryErrors.addError('Something went wrong while trying to retrieve your 2 Factor information, please try again later');
            });
        },
        checkTfaCode: function() {
            this.$validator.errors.clear();

            // Check code server side
            return this.$http.put('v1.1/auth/2fa/secret', {
                oneTimeCode: this.tfaCode,
                secret: this.tfaSecret,
                verifyHash: this.tfaVerifyHash,
                authorization: this.token
            }).then(response => {
                this.tfaRecoveryCodes = response.body.data.recoveryCodes;

                // Enable next step
                return true;
            }, response => {
                let notifications = JSON.parse(response.bodyText).notifications;

                if (notifications.length > 0) {
                    if (notifications.filter((element) => { return element.message === 'invalid_2fa_secret'; }).length > 0)
                        this.$validator.errors.add({
                            msg: 'Invalid secret (log back in and try again)'
                        });
                    if (notifications.filter((element) => { return element.message === 'invalid_2fa_code'; }).length > 0)
                        this.$validator.errors.add({
                            msg: 'Invalid code'
                        });
                    if (notifications.filter((element) => { return element.message === 'updating_user_2fa_information'; }).length > 0)
                        this.$validator.errors.add({
                            msg: 'Something went wrong while checking your code, please try again later'
                        });
                }

                return false;
            }).catch(() => {
                this.$validator.errors.add({
                    msg: 'Something went wrong while checking your code, please try again later'
                });

                return false;
            });
        }
    }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
    #tfa_code_check {
        width: 100%;
        margin-top: 15px;
    }

    .wrapper {
        padding: 3em 20em;
    }

    .cancel-btn,
    .previous-btn {
        background-color: $secondary-color !important;
        border-color: $secondary-color !important;
        color: white;
    }

    .next-btn,
    .finish-btn {
        background-color: $primary-color !important;
        border-color: $primary-color !important;
        color: white;
    }

    .store-btn {
        text-align: center;
        padding: 2em 0 0 0;

        ul {
            margin: 0;
            list-style: none;

            li {
                display: inline-block;
                vertical-align: top;
                margin: 0 5px;
            }
        }
    }

    .qr-code {
        text-align: right;
    }

    .qr-text {
        text-align: left;
    }

    .tfaCodeWrapper {
        width: 15em;
        margin: 0 auto 1em auto;
    }

    #top-logo {
        padding: 0 0 1em 0;
    }

    #recovery-codes {
        padding: 1em 0 0 0;

        ul {
            padding: 0;
            list-style: none;
            width: 19em;
            margin: .5em auto 0 auto;

            $border: 1px solid grey;
            li {
                width: 31%;
                display: inline-block;
                vertical-align: top;
                margin: 0 0 6px 0;
                border: $border;
                border-left: 0;
                text-align: center;
                color: #697c9d;
                font-weight: 500;
                font-size: 14px;

                &:nth-child(3n+1) {
                    border-left: $border;
                }
            }
        }
    }

    .errors {
        small {
            color: red;
        }
        padding-top: 1em;
    }
</style>

<!-- This is used to override the input from the custom component
     Notice the lack of the "scoped" attribute, this allows us to override child css -->
<style lang="scss">
    input[name='tfaCode'] {
        text-align: center;
        background-color: #ffffff;
    }
    .wizard-tab-container {
        padding: 3em 0 2em 0;
        text-align: center;
    }

    .wizard-title {
        font-size: 1.5em;
    }

    .wizard-icon {
        font-style: unset !important;
    }
</style>
